import { documentsApi } from 'api/documents'
import { DocumentGroup, DocumentType } from 'api/documents/types'
import { useApiRequest } from 'hooks/useApiRequest'
import { useCompanyId } from 'hooks/useCompanyId'
import { useEffect, useState } from 'react'
import { useCompanyInfo } from 'ui/hooks/useCompanyInfo'

type DocumentsType = Record<
  keyof typeof DocumentType,
  {
    actual: DocumentGroup
    archieved: DocumentGroup[]
  }
>

const defaultDocumentsByType = {
  Agreement: {
    actual: { status: 'InReview', files: [] },
    archieved: [],
  },
  RegistrationCertificate: {
    actual: { status: 'InReview', files: [] },
    archieved: [],
  },
  SignatureAuthority: {
    actual: { status: 'InReview', files: [] },
    archieved: [],
  },
  PassportOrIdCard: {
    actual: { status: 'InReview', files: [] },
    archieved: [],
  },
} as DocumentsType

export const useInitDocuments = () => {
  const companyId = useCompanyId()
  const [documents, setDocuments] = useState<DocumentsType>(
    defaultDocumentsByType
  )
  const [reloadKey, setReloadKey] = useState(0)
  const getDocumentsRequest = useApiRequest(
    () => documentsApi.getDocuments(String(companyId)),
    undefined,
    false,
    undefined,
    true
  )

  const { getCompanyInfo } = useCompanyInfo(false)

  useEffect(() => {
    const fetchDocuments = async () => {
      const response = await getDocumentsRequest.apiRequest()
      if (response) {
        const transformedData: DocumentsType = structuredClone(
          defaultDocumentsByType
        )

        response.data.groups?.forEach((group) => {
          group.files?.forEach((file) => {
            const docType =
              file.supplierDocumentType as keyof typeof DocumentType

            if (file.isArchived) {
              const existingGroup = transformedData[docType].archieved.find(
                (g) => g.date === group.date
              )
              if (existingGroup) {
                existingGroup.files.push(file)
              } else {
                transformedData[docType].archieved.push({
                  status: group.status,
                  date: group.date,
                  files: [file],
                })
              }
            } else {
              transformedData[docType].actual.files.push(file)
              transformedData[docType].actual.status = group.status
              transformedData[docType].actual.date = group.date
            }
          })
        })
        setDocuments(transformedData as DocumentsType)
      }
    }
    fetchDocuments()
  }, [reloadKey])

  const onReload = () => {
    setReloadKey((key) => key + 1)
    getCompanyInfo(Number(companyId))
  }

  return {
    documents,
    onReload,
    loading: getDocumentsRequest.loading && !getDocumentsRequest.isLoaded,
  }
}
